import Web3 from "web3";
import { useWeb3React } from "@web3-react/core";

import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import PresaleOverview from "../Components/PresaleManage/PresaleOverview";
import PresalesTools from "../Components/PresaleManage/PresalesTools";
import PresaleSteps from "../Components/PresaleManage/PresaleSteps";
import PresaleExclude from "../Components/PresaleManage/PresaleExclude";
import PresaleDeposit from "../Components/PresaleManage/PresaleDeposit";
import LoadingBar from "../Components/UI/LoadingBar";
import PresaleDetails from "../Components/PresaleManage/PresaleDetails";
import AddRemoveWhitelist from "../Components/PresaleManage/AddRemoveWhitelist";

import { getEllipsisTxt } from "../helpers/formatters";

import PresaleABI from "../abis/ABI_Presale.json";
import ABI_StorageLaunchpad from "../abis/ABI_StorageLaunchpad.json";
import ABI_LIQUIDITY_LOCK_PERSONAL from "../abis/presale/ABI_LIQUIDITY_LOCK_PERSONAL.json";
import { getPresaleDetails } from "../utils/apis";
import { useNetworkScanURL, useStorageContractAddress } from "../hooks";
import LinkWithSearchParams from "../Components/UI/LinkWithSearchParams";

const ManagePresale = () => {
    const NETWORK_SCAN_URL = useNetworkScanURL();
    const contractUrlOnScan = (_address) => {
        return `${NETWORK_SCAN_URL}${_address}`;
    };
    const STORAGE_CONTRACT_ADDRESS = useStorageContractAddress();
    const [searchParams] = useSearchParams();

    const [detailModalVisible, setDetailModalVisible] = useState(
        searchParams.get("detailmodal") === "true"
    );
    const [whitelistModalVisible, setWhitelistModalVisible] = useState(false);
    const [presaleContract, setPresaleContract] = useState(null);
    const [lockerLPContract, setLockerLPContract] = useState(null);
    const [presaleAddress, setPresaleAddress] = useState(null);

    const { active, account, library } = useWeb3React();

    const [tokenDetails, setTokenDetails] = useState(null);

    const [checkLoading, setcheckLoading] = useState(true);
    const [loadingMainData, setLoadingMainData] = useState(true);
    const [offChainData, setOffChainData] = useState({});
    useEffect(() => {
        console.log("active", active);
        if (active === true) {
            window.web3 = new Web3(library.provider);
            checkPresaleAddress();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, STORAGE_CONTRACT_ADDRESS]);

    const checkPresaleAddress = async () => {
        const web3 = window.web3;
        const _storageContract = new web3.eth.Contract(
            ABI_StorageLaunchpad,
            STORAGE_CONTRACT_ADDRESS
        );
        const _totalProjects = await _storageContract.methods
            .getProjectsCount()
            .call();
        const _0toNarr = Array.from(
            { length: _totalProjects },
            (_, index) => index
        );
        const presaleAddresses = await Promise.all(
            _0toNarr.map((index) =>
                _storageContract.methods.entryListByPresaleAddress(index).call()
            )
        );
        const presaleContracts = presaleAddresses.map(
            (address) => new web3.eth.Contract(PresaleABI, address)
        );
        const owners = await Promise.all(
            presaleContracts.map((contract) => contract.methods.owner().call())
        );
        const indexOf = owners.indexOf(account);
        if (indexOf >= 0) {
            setPresaleAddress(presaleAddresses[indexOf]);
            setPresaleContract(presaleContracts[indexOf]);
        }
        setcheckLoading(false);
    };

    useEffect(() => {
        if (presaleContract != null) {
            loadData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [presaleContract]);

    const loadData = async () => {
        const web3 = window.web3;

        let [
            _offChainData,
            _tokenName,
            _tokenSymbol,
            _token,
            _tokenHardCap,
            _weiRaised,
            _tokenMaxContribution,
            _tokenMinContribution,
            _isWhitelist,
            _lockerLP,
        ] = await Promise.all([
            getPresaleDetails(presaleAddress),
            presaleContract.methods.tokenName().call(),
            presaleContract.methods.tokenSymbol().call(),
            presaleContract.methods.token().call(),
            presaleContract.methods.tokenHardCap().call(),
            presaleContract.methods.weiRaised().call(),
            presaleContract.methods.tokenMaxContribution().call(),
            presaleContract.methods.tokenMinContribution().call(),
            presaleContract.methods.isWhitelist().call(),
            presaleContract.methods.lockerLP().call(),
        ]);

        setOffChainData(_offChainData);

        const _lockerLPContract = new web3.eth.Contract(
            ABI_LIQUIDITY_LOCK_PERSONAL,
            _lockerLP
        );
        if (parseInt(_lockerLP) === 0) setLockerLPContract(null);
        else setLockerLPContract(_lockerLPContract);

        const tokenArray = [
            {
                name: "Name",
                text: _tokenName,
            },
            {
                name: "Symbol",
                text: _tokenSymbol,
            },
            {
                name: "Token Address",
                text: (
                    <a
                        href={contractUrlOnScan(_token)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-kadGrSt hover:opacity-50 duration-150"
                    >
                        {getEllipsisTxt(_token)}
                    </a>
                ),
            },
            {
                name: "Presale Address",
                text: (
                    <a
                        href={contractUrlOnScan(presaleAddress)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-kadGrSt hover:opacity-50 duration-150"
                    >
                        {getEllipsisTxt(presaleAddress)}
                    </a>
                ),
            },
            {
                name: "Presale Link",
                text: (
                    <LinkWithSearchParams
                        className="text-kadGrSt hover:opacity-50 duration-150"
                        to={{
                            pathname: `/dashboard/presale/${presaleAddress}`,
                        }}
                    >
                        🔗 Here
                    </LinkWithSearchParams>
                ),
            },
        ];

        let _item = {
            tokenArray: tokenArray,
            tokenAddress: _token,
            weiRaised: window.web3.utils.fromWei(
                _weiRaised.toString(),
                "ether"
            ),
            hardCap: _tokenHardCap,
            minContribution: window.web3.utils.fromWei(
                _tokenMinContribution.toString(),
                "ether"
            ),
            maxContribution: window.web3.utils.fromWei(
                _tokenMaxContribution.toString(),
                "ether"
            ),
            isWhitelist: _isWhitelist,
            lockerLP: _lockerLP,
        };
        setTokenDetails(_item);
        setLoadingMainData(false);
    };

    return (
        <div className="flex flex-col main-container animation-fade-in">
            {checkLoading ? (
                <div className="text-black dark:text-white">
                    <LoadingBar text={"🔎 Finding Your Presale..."} />
                </div>
            ) : presaleAddress !== null ? (
                loadingMainData ? (
                    <div className="text-black dark:text-white">
                        <LoadingBar text={"Loading Presale Information..."} />
                    </div>
                ) : (
                    <>
                        <div className="flex flex-col lg:flex-row gap-6 mb-6">
                            <div className="flex flex-col w-full lg:w-[30%] gap-4">
                                <PresaleOverview tokenDetails={tokenDetails} />
                            </div>

                            <div className="w-full lg:w-[43%]">
                                <PresalesTools
                                    tokenDetails={tokenDetails}
                                    setDetailModalVisible={
                                        setDetailModalVisible
                                    }
                                    setWhitelistModalVisible={
                                        setWhitelistModalVisible
                                    }
                                    account={account}
                                    presaleContract={presaleContract}
                                    lockerLPContract={lockerLPContract}
                                    tokenName={tokenDetails.tokenArray[0].text}
                                    showDetailModal={setDetailModalVisible}
                                />
                            </div>
                            <div className="w-full lg:w-[27%]">
                                <PresaleSteps />
                            </div>
                        </div>
                        <div className="flex flex-col lg:flex-row gap-6">
                            <PresaleExclude presaleAddress={presaleAddress} />
                            <PresaleDeposit
                                presaleContract={presaleContract}
                                presaleAddress={presaleAddress}
                                active={active}
                                account={account}
                                tokenDetails={tokenDetails}
                            />
                        </div>
                        <PresaleDetails
                            presaleAddress={presaleAddress}
                            offChainData={offChainData}
                            setVisible={setDetailModalVisible}
                            visible={detailModalVisible}
                        />
                        <AddRemoveWhitelist
                            account={account}
                            presaleContract={presaleContract}
                            setVisible={setWhitelistModalVisible}
                            visible={whitelistModalVisible}
                        />
                    </>
                )
            ) : (
                <span className="text-center size-2 text-black dark:text-white py-6 font-semibold">
                    ⛔ You don't have a presale yet.. ⛔
                </span>
            )}
        </div>
    );
};
export default ManagePresale;
